import { FC, useEffect, useRef } from 'react';
import { Box, CircularProgress, Paper, Stack } from '@mui/material';
import useElementResize from 'hooks/useElementResize';
import BreadcrumbNavigation from 'components/BreadcrumbNavigation';
import { TBreadcrumbItem } from 'components/BreadcrumbNavigation/BreadcrumbNavigation.types';
import Grid from '@mui/material/Unstable_Grid2';

type TDetailsPageWrapperProps = {
  header: {
    rightAction?: React.ReactNode;
    leftAction?: React.ReactNode;
    breadcrumbItems: TBreadcrumbItem[];
  };
  bodyBgColor?: string;
  children: React.ReactNode;
  loading?: boolean;
  setIsMobileView?: (value: boolean) => void;
  isEmptyView?: boolean;
  emptyView?: React.ReactNode;
};

const DetailsPageWrapper: FC<TDetailsPageWrapperProps> = ({
  header,
  bodyBgColor,
  children,
  loading,
  setIsMobileView,
  isEmptyView,
  emptyView,
}) => {
  const headerRef = useRef<HTMLDivElement>(null);
  const { height: headerHeight, width } = useElementResize({
    ref: headerRef,
  });
  useEffect(() => {
    if (width < 765 && setIsMobileView) {
      setIsMobileView(true);
    } else if (setIsMobileView) setIsMobileView(false);
  }, [width]);

  const pageTopPadding = 24;
  const headerMarginBottom = 16;
  const appBarHeight = 56;
  const bodyDistanceToScreenTop = headerHeight + pageTopPadding + headerMarginBottom + appBarHeight;

  let content = children;
  if (loading) {
    content = (
      <Stack justifyContent="center" alignItems="center" height="100%">
        <CircularProgress sx={{ color: 'primary.main' }} />
      </Stack>
    );
  }
  if (isEmptyView && !loading) {
    content = emptyView;
  }

  return (
    <Box
      sx={{
        p: 3,
        height: '100%',
        maxWidth: '100%',
        backgroundColor: 'background.default',
      }}
    >
      <Box ref={headerRef}>
        <Paper
          elevation={0}
          sx={{
            px: 3,
            py: 2,
            mb: 2,
            borderRadius: 3,
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            ...(bodyBgColor && {
              borderTop: `3px solid ${bodyBgColor}`,
            }),
          }}
        >
          <Grid
            container
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            wrap="wrap"
            spacing={2}
            minHeight={54}
          >
            <Grid>{header.leftAction}</Grid>
            <Grid xs={12} sm="auto" sx={{ order: { xs: 3, sm: 2 } }}>
              <BreadcrumbNavigation items={header.breadcrumbItems} />
            </Grid>
            <Grid sx={{ order: { xs: 2, sm: 3 } }}>{header.rightAction}</Grid>
          </Grid>
        </Paper>
      </Box>
      <Box sx={{ height: `calc(100vh - ${bodyDistanceToScreenTop}px)` }}>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            height: '100%',
            overflowY: 'auto',
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
          }}
        >
          <Box
            sx={{
              height: 1,
              ...(loading && {
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }),
            }}
          >
            {content}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default DetailsPageWrapper;
